import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { transientProps } from '@/app/styles/styledHelpers';

export const CardWrap = styled(Box, { shouldForwardProp: transientProps })<{ $selected?: boolean; $locked?: boolean }>`
  position: relative;
  border-radius: 20px;
  background: #fff;
  overflow: visible;
  transform: translateY(0);
  border: ${({ $selected }) => ($selected ? '3px solid #436fb0' : '')};
  box-shadow: ${({ $selected }) => ($selected ? '0 4px 16px rgba(0,82,204,0.18)' : '0 4px 16px rgba(120,120,120,0.13)')};
  height: 100%;
  display: flex;
  flex-direction: column;
  opacity: ${({ $locked }) => ($locked ? 0.75 : 1)};
  transition:
    transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.5s cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 24px;
    background-size: 400% 400%;
    opacity: 0;
    transition: opacity 0.6s ease;
    z-index: 0;
  }

  &:hover {
    transform: ${({ $locked }) => ($locked ? 'none' : 'translateY(-10px)')};
    box-shadow: ${({ $selected, $locked }) =>
      $locked
        ? '0 4px 16px rgba(120,120,120,0.13)'
        : $selected
          ? '0 24px 48px rgba(0,82,204,0.28), 0 8px 16px rgba(0,82,204,0.14)'
          : '0 24px 48px rgba(120,120,120,0.18), 0 8px 16px rgba(120,120,120,0.10)'};
  }

  &:hover .card-body {
    background-position: 100% 100%;
  }
  &:hover .footer-gradient {
    background-position: 0% 0%;
  }
  &:hover .divider-line {
    width: 80px;
  }
  &:hover .title-text {
    color: #003d99;
  }
`;

export const PricingCard = styled(Box)({
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  zIndex: 1,
});

export const CardBody = styled(Box)({
  backgroundSize: '200% 200%',
  backgroundPosition: '0% 0%',
  transition: 'background-position 0.8s cubic-bezier(0.4,0,0.2,1)',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  paddingBottom: 8,
});

export const TransitionContainer = styled(Box, { shouldForwardProp: transientProps })<{ $selected?: boolean }>(({ $selected }) => ({
  height: 16,
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  flexShrink: 0,
  backgroundSize: '300% 300%',
  backgroundPosition: '100% 100%',
  transition: 'background-position 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '110px',
    height: '100%',
    backgroundColor: $selected ? '#436fb0' : '#e7dfdf',
  },
}));

export const StripesWrapper = styled(Box)({
  position: 'absolute',
  top: 0,
  right: 90,
  height: '100%',
  display: 'flex',
  zIndex: 2,
  '& > div': {
    height: '100%',
    transform: 'skewX(-45deg)',
    transformOrigin: 'bottom left',
  },
});

export const CardFooter = styled(Box, { shouldForwardProp: transientProps })<{ $selected?: boolean }>(({ $selected }) => ({
  color: $selected ? '#fff' : '#000',
  padding: '16px 10px',
  flexShrink: 0,
  background: $selected
    ? 'linear-gradient(135deg, rgb(25 68 133) 0%, rgb(91 142 220) 40%, rgb(139 181 244) 60%, rgb(4 57 137) 100%) 100% 100% / 300% 300%'
    : 'linear-gradient(135deg, rgb(84 84 84 / 0%) 0%, rgb(204 202 202 / 61%) 40%, rgb(111 110 110 / 0%) 60%, rgb(169 167 167 / 50%) 100%) 100% 100% / 300% 300%',
  backgroundSize: '300% 300%',
  backgroundPosition: '100% 100%',
  transition: 'background-position 0.6s cubic-bezier(0.4,0,0.2,1)',
  borderRadius: '0 0 15px 15px',
}));

export const SelectedButton = styled(Button)<{ disabled?: boolean,isContracted:boolean }>(({ disabled ,isContracted}) => ({
  backgroundColor: disabled && !isContracted ? 'white !important' : 'white',
  color: '#0052cc',
  borderRadius: 50,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '1rem',
  padding: '12px 20px',
  width: '100%',
  justifyContent: 'flex-start',
  gap: '12px',
  opacity: disabled ? 0.6 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
  '&:hover': { backgroundColor: disabled ? 'white' : '#f0f4ff' },
  '& .MuiSvgIcon-root': { fontSize: 24 },
}));

export const CornerRibbon = styled(Box, { shouldForwardProp: transientProps })<{ $color: string; $shadowColor: string }>`
  position: absolute;
  top: 0;
  right: -3px;
  width: 130px;
  height: 130px;
  overflow: hidden;
  border-radius: 0 20px 0 0;
  z-index: 10;
  pointer-events: none;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    border-style: solid;
  }

  &::before {
    border-width: 0 130px 130px 0;
    border-color: transparent ${({ $color }) => $color} transparent transparent;
    filter: drop-shadow(-2px 2px 3px ${({ $shadowColor }) => $shadowColor});
  }
`;

export const MobileRibbon = styled(Box, { shouldForwardProp: transientProps })<{ $color: string }>`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${({ $color }) => $color};
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const RibbonText = styled(Box)`
  position: absolute;
  top: 29px;
  right: 0px;
  width: 86px;
  text-align: center;
  transform: rotate(45deg);
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  text-transform: uppercase;
`;
